import { useEffect, useRef } from "react";

const COLORS = [
    [255, 244, 214], // warm white
    [250, 220, 150], // soft gold
    [244, 205, 220], // faint rose
];

export default function FairyDust() {
    const canvasRef = useRef(null);

    useEffect(() => {
        const canvas = canvasRef.current;
        const ctx = canvas.getContext("2d");
        let raf;

        const resize = () => {
            canvas.width = window.innerWidth;
            canvas.height = window.innerHeight;
        };
        resize();
        window.addEventListener("resize", resize);

        const count = window.innerWidth < 768 ? 26 : 46;
        const dust = [];
        const spawn = (initial) => {
            const [r, g, b] = COLORS[Math.floor(Math.random() * COLORS.length)];
            return {
                x: Math.random() * canvas.width,
                y: initial ? Math.random() * canvas.height : canvas.height + 12,
                size: Math.random() * 1.3 + 0.5,
                driftX: (Math.random() - 0.5) * 0.16,
                driftY: -(Math.random() * 0.14 + 0.04),
                phase: Math.random() * Math.PI * 2,
                speed: Math.random() * 0.014 + 0.006,
                color: `${r},${g},${b}`,
            };
        };
        for (let i = 0; i < count; i++) dust.push(spawn(true));

        const draw = () => {
            ctx.clearRect(0, 0, canvas.width, canvas.height);
            for (let i = 0; i < dust.length; i++) {
                const s = dust[i];
                s.phase += s.speed;
                s.x += s.driftX;
                s.y += s.driftY;
                if (s.y < -14 || s.x < -14 || s.x > canvas.width + 14) {
                    dust[i] = spawn(false);
                    continue;
                }
                const wave = Math.sin(s.phase); // -1..1
                const alpha = 0.10 + (wave + 1) * 0.20; // gentle breathing glow

                const glow = ctx.createRadialGradient(s.x, s.y, 0, s.x, s.y, s.size * 5);
                glow.addColorStop(0, `rgba(${s.color},${alpha})`);
                glow.addColorStop(1, `rgba(${s.color},0)`);
                ctx.fillStyle = glow;
                ctx.beginPath();
                ctx.arc(s.x, s.y, s.size * 5, 0, Math.PI * 2);
                ctx.fill();

                ctx.beginPath();
                ctx.arc(s.x, s.y, s.size, 0, Math.PI * 2);
                ctx.fillStyle = `rgba(${s.color},${Math.min(1, alpha * 1.6)})`;
                ctx.fill();

                // occasional four-point star glint at the crest of the twinkle
                if (wave > 0.86) {
                    const star = ((wave - 0.86) / 0.14) * 0.9;
                    const len = s.size * 7;
                    ctx.strokeStyle = `rgba(${s.color},${star})`;
                    ctx.lineWidth = 0.7;
                    ctx.beginPath();
                    ctx.moveTo(s.x - len, s.y);
                    ctx.lineTo(s.x + len, s.y);
                    ctx.moveTo(s.x, s.y - len);
                    ctx.lineTo(s.x, s.y + len);
                    ctx.stroke();
                }
            }
            raf = requestAnimationFrame(draw);
        };
        draw();

        return () => {
            cancelAnimationFrame(raf);
            window.removeEventListener("resize", resize);
        };
    }, []);

    return <canvas ref={canvasRef} data-testid="fairy-dust-canvas" className="particle-canvas" style={{ zIndex: 4 }} />;
}
